import { configureChannelContext } from 'shared/channels';
import {
  getCurrentController,
  getLastServiceWorkerPong,
  markServiceWorkerStale,
} from 'shared/channels/ensureServiceWorkerAwake';
import { wakeServiceWorkerBeforeRequest } from 'shared/channels/wakeServiceWorkerBeforeRequest';
import { logError } from 'setup/logger/logError';
import { logInfo } from 'setup/logger/logInfo';
import { reportUnresponsiveServiceWorker } from './diagnostics';

/**
 * How the page talks to the service worker: wake it before every request (a
 * broadcast does not start a stopped worker), and on a timeout log a snapshot
 * of the worker and IndexedDB state that explains the missing response.
 */
export function configurePageChannels(): void {
  configureChannelContext({
    clientEnvironment: {
      prepareToSend: async () => {
        const wakeResult = await wakeServiceWorkerBeforeRequest();
        const controllerAtSend = getCurrentController();
        const sentAt = Date.now();
        const pong = getLastServiceWorkerPong();
        return () => ({
          wakeResult,
          // The worker instance the request went to: a request sent moments after its start was
          // lost while it started; another start time on the retry means it was stopped meanwhile.
          swVersion: pong?.swVersion,
          swStartedAt: pong?.swStartedAt,
          swUptimeMsAtSend: pong ? sentAt - pong.swStartedAt : undefined,
          hadControllerAtSend: controllerAtSend !== null,
          controllerChanged: getCurrentController() !== controllerAtSend,
          visibilityState: document.visibilityState,
          online: navigator.onLine,
        });
      },
      markResponderStale: markServiceWorkerStale,
      reportTimeout: ({ operation, timeoutMs, diagnostics, requestId }) =>
        reportUnresponsiveServiceWorker(operation, { requestId, timeoutMs, ...diagnostics }),
    },
    reportError: logError,
    reportEvent: logInfo,
  });
}
